<template>
  <nav v-if="user?.loggedIn && $route.meta.requiresAuth && user.claims.active"
    class="navbar bg-primary navbar-expand-md m-1 rounded">
    <div class="container">
      <router-link class="navbar-brand d-flex w-25 m-0" :to="`/home`" @click="closeNavbar">
        <Logo :fill="'#ffffff'" :fillRect="'#4088c9'" :height="'32px'" :width="'32px'"
          class="me-2 d-flex align-items-center"></Logo>
        <span id="logotext" class="text-white">
          Portal
        </span>
      </router-link>

      <button class="navbar-toggler" type="button" @click="toggleNavbar" aria-controls="navbarSupportedContent"
        aria-expanded="false" aria-label="Toggle navigation">
        <font-awesome-icon :icon="['far', 'bars']" class="text-white" />
      </button>

      <div class="w-25 d-flex align-items-center justify-content-end d-md-none">
        <Lang class="nav-item me-2" :showHumanLang="false" :rightAlignNav="true" />
        <DropDownAccount v-if="user?.loggedIn && $route.meta.requiresAuth" @onLogout="logOut()" @edit="edit('edit')" />
      </div>

      <div class="collapse navbar-collapse" id="navbarSupportedContent" ref="navbarSupportedContent">
        <ul class="navbar-nav mx-auto" v-if="
          user.claims.state === 'admin' || user.claims.state === 'superadmin'
        ">
          <li class="nav-item">
            <router-link :class="`nav-link ${$route.path.includes('/home') ? 'active' : ''
              }`" :to="`/home`" @click="closeNavbar">
              {{ $t("navbar.tools") }}
            </router-link>
          </li>

          <li class="nav-item">
            <router-link :class="`nav-link ${$route.path.includes('settings') ? 'active' : ''
              }`" :to="`/settings`" @click="closeNavbar">
              {{ $t("navbar.settings") }}
            </router-link>
          </li>

          <li class="nav-item">
            <router-link :class="`nav-link ${$route.path.includes('users') ? 'active' : ''
              }`" :to="`/users`" @click="closeNavbar">
              {{ $t("navbar.usermanagement") }}
            </router-link>
          </li>

          <li class="nav-item" v-if="user.claims.state === 'superadmin'">
            <router-link :class="`nav-link ${$route.path.includes('activities') ? 'active' : ''
              }`" :to="`/activities`" @click="closeNavbar">
              {{ $t("navbar.activities") }}
            </router-link>
          </li>
        </ul>
      </div>

      <div class="w-25 d-none align-items-center justify-content-end d-md-flex">
        <Lang class="nav-item me-2" :showHumanLang="false" :rightAlignNav="true" />
        <DropDownAccount v-if="user?.loggedIn && $route.meta.requiresAuth" @onLogout="logOut()" @edit="edit('edit')" />
      </div>
    </div>
  </nav>

  <div class="modal fade" ref="userEditModal" data-bs-backdrop="static">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t("user.my_data") }}</h5>
        </div>

        <div class="modal-body">
          <label for="name" class="form-label">
            {{ $t("user.name") }}
          </label>
          <div class="input-group mb-3">
            <input class="form-control" id="name" v-model="name" :disabled="loading" />
          </div>

          <label for="email" class="form-label">
            {{ $t("user.email") }}
          </label>
          <div class="input-group">
            <input class="form-control" id="userEmail" disabled v-model="email" />
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="userEditModal.hide()">
            {{ $t("user.close") }}
          </button>
          <button type="button" class="btn btn-primary text-white" @click="saveUserData()" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ $t("user.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Logo from "../assets/Logo";
import { mapGetters } from "vuex";
import { signOut, updateProfile } from "firebase/auth";
import Lang from "../components/smallComponents/LangComponent";
import DropDownAccount from "./smallComponents/DropDownAccount.vue";
import { Modal } from "@comlinedev/bootstrap5";
import store from "../plugins/store";
import { auth } from "../plugins/firebase";

export default {
  name: "NavigationComponent",
  components: {
    Logo,
    Lang,
    DropDownAccount,
  },
  computed: {
    ...mapGetters({
      user: "user",
    }),
  },
  watch: {
    user: {
      handler(newValue) {
        this.name = newValue.claims.name;
        this.email = newValue.claims.email;
      },
      deep: true,
    },
  },
  data() {
    return {
      name: "",
      email: "",
      loading: false,
      userEditModal: null,
    };
  },
  methods: {
    toggleNavbar() {
      const navbarCollapse = this.$refs.navbarSupportedContent;
      if (!navbarCollapse) return;

      if (navbarCollapse.classList.contains('show')) {
        // Close with animation
        navbarCollapse.style.height = navbarCollapse.scrollHeight + 'px';
        navbarCollapse.classList.remove('collapse');
        navbarCollapse.classList.add('collapsing');

        // Force reflow
        navbarCollapse.offsetHeight;

        navbarCollapse.style.height = '0';

        setTimeout(() => {
          navbarCollapse.classList.remove('collapsing', 'show');
          navbarCollapse.classList.add('collapse');
          navbarCollapse.style.height = '';
        }, 350); // Bootstrap's transition duration
      } else {
        // Open with animation
        navbarCollapse.classList.remove('collapse');
        navbarCollapse.classList.add('collapsing');
        navbarCollapse.style.height = '0';

        // Force reflow
        navbarCollapse.offsetHeight;

        const height = navbarCollapse.scrollHeight;
        navbarCollapse.style.height = height + 'px';

        setTimeout(() => {
          navbarCollapse.classList.remove('collapsing');
          navbarCollapse.classList.add('collapse', 'show');
          navbarCollapse.style.height = '';
        }, 350);
      }
    },
    closeNavbar() {
      if (window.innerWidth < 768) {
        const navbarCollapse = this.$refs.navbarSupportedContent;
        if (!navbarCollapse || !navbarCollapse.classList.contains('show')) return;

        // Close with animation
        navbarCollapse.style.height = navbarCollapse.scrollHeight + 'px';
        navbarCollapse.classList.remove('collapse');
        navbarCollapse.classList.add('collapsing');

        navbarCollapse.offsetHeight;

        navbarCollapse.style.height = '0';

        setTimeout(() => {
          navbarCollapse.classList.remove('collapsing', 'show');
          navbarCollapse.classList.add('collapse');
          navbarCollapse.style.height = '';
        }, 350);
      }
    },
    edit() {
      this.userEditModal.show();
    },
    saveUserData() {
      this.loading = true;
      updateProfile(auth.currentUser, {
        displayName: this.name,
      })
        .then(() => {
          this.$store.commit(
            "addSuccess",
            `Dein Name wurde zu "${this.name}" geändert.`
          );
          let newUser = this.user;
          newUser.claims.name = this.name;
          store.dispatch("fetchUser", newUser.data);
          this.userEditModal.hide();
        })
        .catch((error) => {
          this.$store.commit("addError", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    logOut() {
      signOut(auth)
        .then(() => {
          this.$router.push("login");
        })
        .catch((error) => {
          this.$store.commit("addError", error);
        });
    },

  },
  mounted() {
    this.$nextTick(() => {
      this.userEditModal = new Modal(this.$refs.userEditModal);
    });
  },
};
</script>

<style lang="css">
.navbar .nav-link {
  color: rgba(255, 255, 255, 0.55) !important;
}

.navbar .nav-link:hover,
.navbar .nav-link:focus,
.navbar .nav-link.active {
  color: rgba(255, 255, 255, 1) !important;
}
</style>
